import { Calculator, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { formatEuros } from '@/lib/format'
import { usePreviewDistributionScript } from '@/lib/clients-api'
import type { ClientDetail, DistributionPreviewResult } from '@/lib/clients-types'

export function DistributionCard({ client }: { client: ClientDetail }) {
  const [showInstruction, setShowInstruction] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const script = client.distributionScript
  if (!script) return null
  return (
    <Card>
      <CardHeader
        title="Verdeling"
        subtitle={script.isDefault ? 'Default' : `Ingesteld via MCP · versie ${script.version}`}
        action={
          <button type="button" className="btn-ghost h-8 px-2 text-sm" onClick={() => setPreviewOpen(true)} data-testid="distribution-preview-button">
            <Calculator className="h-4 w-4" /> Proefberekening
          </button>
        }
      />
      <div className="mt-4 space-y-3">
        <p className="text-sm text-ink-soft" data-testid="distribution-summary">{script.summary}</p>
        {!script.isDefault ? (
          <p className="label-caps text-ink-faint">
            Versie {script.version}
            {script.createdBy ? ` · ${script.createdBy}` : ''}
            {script.createdAt ? ` · ${new Date(script.createdAt).toLocaleDateString('nl-NL')}` : ''}
          </p>
        ) : null}
        {script.instruction ? (
          <div className="rounded-lg bg-fill-1 px-3 py-2">
            <button type="button" className="label-caps text-ink-faint" onClick={() => setShowInstruction((v) => !v)}>
              {showInstruction ? 'Instructie verbergen' : 'Instructie tonen'}
            </button>
            {showInstruction ? <p className="mt-1 text-sm text-ink-soft">{script.instruction}</p> : null}
          </div>
        ) : null}
        <p className="text-xs text-ink-faint">Aanpassen kan alleen via MCP (distribution_script_upsert).</p>
      </div>
      {previewOpen ? <ScriptPreviewModal client={client} onClose={() => setPreviewOpen(false)} /> : null}
    </Card>
  )
}

function rangeForMonth(month: string): { start: string; end: string } {
  const [y, m] = month.split('-').map(Number)
  const endDate = new Date(y, m, 0)
  return {
    start: `${y}-${String(m).padStart(2, '0')}-01`,
    end: `${y}-${String(m).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`,
  }
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function currentMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })
}

function ScriptPreviewModal({ client, onClose }: { client: ClientDetail; onClose: () => void }) {
  const [month, setMonth] = useState(currentMonth)
  const [result, setResult] = useState<DistributionPreviewResult | null>(null)
  const preview = usePreviewDistributionScript(client.id)

  // Herbereken automatisch zodra de maand wijzigt; de eerste render telt mee.
  // biome-ignore lint/correctness/useExhaustiveDependencies: alleen de maand hoort te herberekenen; preview wisselt van referentie en zou blijven rondlopen
  useEffect(() => {
    const { start, end } = rangeForMonth(month)
    preview.mutate({ periodStart: start, periodEnd: end }, { onSuccess: setResult })
  }, [month])

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Proefberekening verdeling"
      footer={
        <button type="button" className="btn-ghost" onClick={onClose}>
          Sluiten
        </button>
      }
    >
      <div className="space-y-4">
        <div>
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Maand</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-ghost h-9 w-9 shrink-0 justify-center px-0"
              aria-label="Vorige maand"
              onClick={() => setMonth((m) => shiftMonth(m, -1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <input
              type="month"
              className="input flex-1 text-center"
              value={month}
              onChange={(e) => {
                if (e.target.value) setMonth(e.target.value)
              }}
              data-testid="distribution-preview-month"
            />
            <button
              type="button"
              className="btn-ghost h-9 w-9 shrink-0 justify-center px-0"
              aria-label="Volgende maand"
              onClick={() => setMonth((m) => shiftMonth(m, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-1.5 text-xs text-ink-faint">
            {client.distributionScript?.isDefault ? 'Default-script' : `Opgeslagen script (versie ${client.distributionScript?.version})`} over{' '}
            {monthLabel(month)}. Er wordt niets opgeslagen.
          </p>
        </div>
        <div className="overflow-hidden rounded-lg border border-hairline">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline bg-fill-1">
                <th className="px-3 py-2 text-left label-caps text-ink-faint">Ontvanger</th>
                <th className="px-3 py-2 text-right label-caps text-ink-faint">Bedrag</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {preview.isPending ? (
                <tr>
                  <td colSpan={2} className="px-3 py-3 text-sm text-ink-faint">
                    Rekenen…
                  </td>
                </tr>
              ) : !result || result.lines.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-3 py-3 text-sm text-ink-faint">
                    Geen uren of bedragen in deze maand.
                  </td>
                </tr>
              ) : (
                result.lines.map((line) => (
                  <tr key={line.beneficiaryLabel}>
                    <td className="px-3 py-2">
                      <p className="font-medium text-ink">{line.beneficiaryLabel}</p>
                      <p className="mt-0.5 text-xs text-ink-faint">{line.explanation}</p>
                    </td>
                    <td className="money px-3 py-2 text-right align-top">{formatEuros(Number(line.amount))}</td>
                  </tr>
                ))
              )}
            </tbody>
            {result && result.lines.length > 0 ? (
              <tfoot>
                <tr className="border-t border-hairline bg-fill-1">
                  <td className="px-3 py-2 text-sm font-medium text-ink">Subtotaal</td>
                  <td className="money px-3 py-2 text-right font-medium">{formatEuros(Number(result.subtotal))}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </div>
    </Modal>
  )
}
